import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { Dialog, DialogActions, DialogTitle, Button, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import LoadingSpinner from '../../../Loading/component';
import Sidebar from '../../../Navigation/Sidebar/Sidebar';
import './WarehouseAssortmentDetailsComponent.css';
import filedownload from "../../../../assests/filedownload.svg";
import calandericon from "../../../../assests/calandericon.svg";
import dragdropimage from "../../../../assests/dragdropimage.svg";
import deleteIconAssortment from "../../../../assests/deleteIconAssortment.svg";
import threeDotIcon from "../../../../assests/threeDotIcon.svg";
import menuFilter from "../../../../assests/menuiFilter.svg";
import sloticon from "../../../../assests/sloticon.svg";
import slotdoubleicon from "../../../../assests/slotdoubleicon.svg";

const CohortAssortmentDetailsComponent = ({
  assortmentDetails,
  loading,
  activeTabIndex,
  onTabChange,
  hasChanges,
  onSaveChanges,
  onCancelChanges,
  handleEndAssortment,
  setShowDeleteModal,
  showDeleteModal,
  categories,
  selectedCategory,
  onCategoryChange,
  currentItems,
  remainingItems,
  onItemDrop,
  itemsContainerRef,
  autoScrollDirection,
  onDragUpdate,
  handleCheckRule,
  assortmentSlotDetails = [],
}) => {
  const navigate = useNavigate();
  const { assortmentId } = useParams();
  const [showMenu, setShowMenu] = useState(false);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [userRole, setUserRole] = useState('');
  const menuRef = useRef();
  const dragItemRef = useRef(null);
  const tabsContainerRef = useRef(null);

  // Check if user has read-only permissions (no edit permissions but can download)
  const isReadOnlyUser = ['manager', 'city lead'].includes(userRole?.toLowerCase());
  
  // Check if user can download (all roles including manager and city lead)
  const canDownload = ['admin', 'est', 'manager', 'city lead'].includes(userRole?.toLowerCase());

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("data"));
    if (userData) {
      setUserRole(userData.details.userRole.role_name);
    }
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Carousel navigation functions
  const scrollTabs = (direction) => {
    if (!tabsContainerRef.current) return;
    
    const container = tabsContainerRef.current;
    const scrollAmount = 300; // Adjust based on your tab width
    
    if (direction === 'left') {
      container.scrollLeft -= scrollAmount;
      setCarouselIndex(prev => Math.max(0, prev - 1));
    } else {
      container.scrollLeft += scrollAmount;
      setCarouselIndex(prev => prev + 1);
    }
  };

  const canScrollLeft = carouselIndex > 0;
  const canScrollRight = carouselIndex < (assortmentDetails.hardware_type_items?.length || 0);

  // Hardware tabs data including primary tab
  const hardwareTabs = [
    {
      id: 0,
      title: "Primary",
      count: assortmentDetails.priority_wise_items?.length || 0,
      type: "primary"
    },
    ...(assortmentDetails.hardware_type_items?.map((item, index) => ({
      id: index + 1,
      title: item.hardware_type,
      count: item.priority_wise_items?.length || 0,
      type: "hardware"
    })) || [])
  ];

  const handleDragStart = (e, item, sourceType) => {
    if (isReadOnlyUser) {
      e.preventDefault();
      return;
    }
    setDraggedItem(item);
    setDragSource(sourceType);
    dragItemRef.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.mv_id);
    e.currentTarget.classList.add('dragging');
  };

  const handleDragOver = (e, index) => {
    if (isReadOnlyUser) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const targetElement = e.currentTarget;
    const boundingRect = targetElement.getBoundingClientRect();
    const offsetY = e.clientY - boundingRect.top;
    const middleThreshold = boundingRect.height / 2;

    targetElement.classList.remove('drag-over-top', 'drag-over-bottom');

    if (offsetY < middleThreshold) {
      targetElement.classList.add('drag-over-top');
    } else {
      targetElement.classList.add('drag-over-bottom');
    }

    if (onDragUpdate) {
      onDragUpdate(e);
    }
  };

  const handleDragLeave = (e) => {
    if (isReadOnlyUser) return;
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over-top', 'drag-over-bottom');
  };

  const handleDrop = (e, targetIndex) => {
    if (isReadOnlyUser) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over-top', 'drag-over-bottom');

    if (draggedItem && dragSource) {
      onItemDrop(draggedItem, targetIndex, dragSource);
    }

    setDraggedItem(null);
    setDragSource(null);
  };

  const handleDragEnd = (e) => {
    if (isReadOnlyUser) return;
    e.currentTarget.classList.remove('dragging');
    setDraggedItem(null);
    setDragSource(null);
  };

  const handleCloneAssortment = () => {
    if (isReadOnlyUser) return;
    navigate(`/home/cohort/assortment/clone/${assortmentId}`);
  };

  const handleDownload = () => {
    if (!canDownload) {
      return;
    }
    const generateCSVContent = () => {
      const {
        id = '',
        reference_id = '',
        reference_name = '',
        start_date = '',
        priority_wise_items = [],
        rule_failures = [],
        hardware_type_items = [],
        created_by_name = '',
        status = '',
        created_date = '',
        updated_by_name = '',
        last_updated_date = ''
      } = assortmentDetails;

      const headers = [
        'Assortment ID',
        'Cohort Name (cohort ID)',
        'Assortment Status',
        'Start Date',
        'VMs Linked',
        'Product Rank',
        'SKU ID',
        'Product Name',
        'Brand Name',
         'MRP',
        'Variant',
        'Item Category',
        'Rule Details',
        'Created By',
        'Created Date',
        'Last Updated By',
        'Last Updated Date'
      ];

      const mainData = [
        id,
        reference_name,
        reference_id ? `${reference_name} ${reference_id}` : '',
        status,
        formatDate(start_date),
        hardware_type_items.length,
        '', '', '', '', '', '', '',
        created_by_name,
        formatDate(created_date),
        updated_by_name,
        formatDate(last_updated_date)
      ].map(value => `"${value}"`).join(',');

      const productRows = priority_wise_items.map((item, index) => {
        const hasRuleFailure = rule_failures.some(rule =>
          rule.failedItems?.ids?.includes(item.mv_id)
        );

        return [
          '', '', '', '', '',
          index + 1,
          item.mv_id || '',
          item.variant_name || '',
          item.brand_name || '',
           item.mrp || item.offer_price || '',
          item.variant_name || '',
          item.categories?.join(', ') || '',
          hasRuleFailure ? 'No' : 'Yes',
          '', '', '', ''
        ].map(value => `"${value}"`).join(',');
      });

      return [headers.join(','), mainData, ...productRows].join('\r\n');
    };

    const csvContent = generateCSVContent();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Cohort_Assortment_${assortmentDetails.id || 'details'}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEndAssortmentWithCheck = () => {
    if (isReadOnlyUser) return;
    handleEndAssortment();
  };

  const handleCheckRuleWithCheck = () => {
    if (isReadOnlyUser) return;
    handleCheckRule();
  };

  const handleCategoryChangeWithCheck = (category) => {
    if (isReadOnlyUser) return;
    onCategoryChange(category);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderSlotIcon = (rank) => {
    if (activeTabIndex === 0 || !Array.isArray(assortmentSlotDetails)) return null;

    const matchedSlot = assortmentSlotDetails.find(slot => {
      const expectedRank = slot.slotId % 100;
      return expectedRank === rank;
    });

    if (!matchedSlot) return null;

    return (
      <div className="warehouse-assortment-slot-icon">
        <img
          src={matchedSlot.isMerged ? slotdoubleicon : sloticon}
          alt={matchedSlot.isMerged ? "Merged Slot" : "Single Slot"}
          style={{ width: "24px", height: "24px" }}
        />
      </div>
    );
  };

  const handleEditAssortment = () => {
    if (isReadOnlyUser) return;
    navigate(`/home/assortment/cohortassortmenteditdetails/${assortmentId}`);
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="warehouse-assortment-details-container">
          <Sidebar />
          <div className="warehouse-assortment-content-wrapper">
            <div className="warehouse-assortment-header-container">
              <div className="warehouse-assortment-header-left-section">
                <button
                  className="warehouse-assortment-back-button"
                  onClick={() => navigate(-1)}
                >
                  ←
                </button>
                <div className="warehouse-assortment-title-section">
                  <h1 className="warehouse-assortment-title">
                    Assortment Id: <strong>{assortmentDetails.id || 'N/A'}</strong>
                  </h1>
                  <p className="warehouse-assortment-created-by">
                    Created By: {assortmentDetails.created_by_name} {assortmentDetails.created_by || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="warehouse-assortment-header-actions">
                {!isReadOnlyUser && assortmentDetails.status === "active" && (
                  <>
                    <button onClick={handleCloneAssortment} className="warehouse-assortment-clone-button">
                      Clone  & Schedule
                     
                    </button>
                    {canDownload && (
                      <button className="warehouse-assortment-download-button" onClick={handleDownload}>
                        <img src={filedownload} alt="Download" />
                      </button>
                    )}
                  </>
                )}

                {!isReadOnlyUser && assortmentDetails.status === "scheduled" && (
                  <>
                    {activeTabIndex === 0 ? (
                      <>
                        <button className="warehouse-assortment-filter-button">
                          <img
                            src={menuFilter}
                            alt="Filter"
                            onClick={() => setShowFilterPopup(true)}
                            title="Filter by category"
                          />
                        </button>
                        <button className="warehouse-assortment-clone-button" onClick={handleEditAssortment}>
                          Edit
                        </button>
                        <Dialog
                          open={showFilterPopup}
                          onClose={() => setShowFilterPopup(false)}
                          className="filter-dialog"
                          PaperProps={{ className: "filter-popup-paper" }}
                        >
                          <DialogTitle className="filter-popup-title">Select Category</DialogTitle>
                          <RadioGroup
                            value={selectedCategory}
                            onChange={(e) => {
                              handleCategoryChangeWithCheck(e.target.value);
                              setShowFilterPopup(false);
                            }}
                          >
                            {categories.map((category) => (
                              <FormControlLabel
                                key={category}
                                value={category}
                                control={<Radio />}
                                label={category}
                                className="filter-radio-label"
                              />
                            ))}
                          </RadioGroup>
                        </Dialog>
                        <div className="menu-wrapper" ref={menuRef}>
                          <button
                            className="warehouse-assortment-download-button"
                            onClick={() => setShowMenu(!showMenu)}
                            title="More options"
                          >
                            <img src={threeDotIcon} alt="More" />
                          </button>
                          {showMenu && (
                            <div className="menu-popup">
                              {canDownload && (
                                <div className="menu-item" onClick={handleDownload}>
                                  <img src={filedownload} alt="Download" className="menu-icon" />
                                  <span>Download</span>
                                </div>
                              )}
                              <div className="menu-item" onClick={() => setShowDeleteModal(true)}>
                                <img src={deleteIconAssortment} alt="Delete" className="menu-icon" />
                                <span>Delete</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      canDownload && (
                        <button className="warehouse-assortment-download-button" onClick={handleDownload}>
                          <img src={filedownload} alt="Download" />
                        </button>
                      )
                    )}
                  </>
                )}

                {!isReadOnlyUser && assortmentDetails.status === "inactive" && (
                  <>
                    <button className="warehouse-assortment-clone-button" onClick={handleCloneAssortment}>
                    
                      Clone & Schedule
                    </button>
                    {canDownload && (
                      <button className="warehouse-assortment-download-button" onClick={handleDownload}>
                        <img src={filedownload} alt="Download" />
                      </button>
                    )}
                  </>
                )}

                {/* Show download button for read-only users (manager, city lead) */}
                {isReadOnlyUser && canDownload && (
                  <button className="warehouse-assortment-download-button" onClick={handleDownload}>
                    <img src={filedownload} alt="Download" />
                  </button>
                )}

                {isReadOnlyUser && (
                  <div className="warehouse-assortment-read-only-badge">
                    Read Only View
                  </div>
                )}
              </div>
            </div>
            
            {!isReadOnlyUser && (
              <Dialog
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                className="custom-delete-dialog"
              >
                <DialogTitle className="custom-delete-dialog-title">
                  Are you sure you want to Delete This Assortment Id: <strong>{assortmentDetails.id}</strong>?
                </DialogTitle>
                <DialogActions className="custom-delete-dialog-actions">
                  <Button onClick={() => setShowDeleteModal(false)} variant="outlined">
                    No
                  </Button>
                  <Button onClick={handleEndAssortmentWithCheck} variant="contained" color="error">
                    Yes
                  </Button>
                </DialogActions>
              </Dialog>
            )}

            <div className="warehouse-assortment-main-content">
              <div className="warehouse-assortment-left-section">
                <div className="warehouse-assortment-info-section">
                  <div className="warehouse-assortment-date-status">
                    <img
                      src={calandericon}
                      alt="Calendar"
                      className="warehouse-assortment-calendar-icon"
                    />
                    <div className="warehouse-assortment-date-info">
                      <span className="warehouse-assortment-date-label">Started On</span>
                      <div className="warehouse-assortment-date-value">
                        {formatDate(assortmentDetails.start_date)}
                      </div>
                    </div>
{
assortmentDetails.status === "inactive" &&(
  <div className="warehouse-assortment-date-info">
                      <span className="warehouse-assortment-date-label">End Date</span>
                      <div className="warehouse-assortment-date-value">
                        {formatDate(assortmentDetails.updated_at)}
                      </div>
                    </div>
  )
}

                 
                    <span className={`warehouse-assortment-status ${assortmentDetails.status?.toLowerCase()}`}>
                      {assortmentDetails.status}
                    </span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
                    {hardwareTabs.length > 0 && (
                      <div className="warehouse-assortment-hardware-tabs-container">
                        <div className="warehouse-assortment-hardware-tabs-wrapper">
                          <button
                            className="carousel-button"
                            onClick={() => scrollTabs('left')}
                            disabled={!canScrollLeft}
                            title="Scroll left"
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                            </svg>
                          </button>
                          
                          <div 
                            className="warehouse-assortment-hardware-tabs"
                            ref={tabsContainerRef}
                          >
                            {hardwareTabs.map((tab, index) => (
                              <div
                                key={tab.id}
                                className={`warehouse-assortment-hardware-tab ${activeTabIndex === tab.id ? "active" : ""}`}
                                onClick={() => onTabChange(tab.id)}
                              >
                                <div className="warehouse-assortment-tab-title">
                                  {tab.title}
                                </div>
                                <div className="warehouse-assortment-tab-count">
                                  {tab.count} Items
                                </div>
                              </div>
                            ))}
                          </div>

                          <button
                            className="carousel-button"
                            onClick={() => scrollTabs('right')}
                            disabled={!canScrollRight}
                            title="Scroll right"
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    {assortmentDetails.rule_failures?.length > 0 && (
                      <div className="warehouse-assortment-rule-failures">
                        <div style={{ display: "flex", flexDirection: "column", margin: "0px" }}>
                          <h2 className="warehouse-assortment-rule-failures-title">
                            {assortmentDetails.rule_failures.length} Rule Fail <br />
                          </h2>
                          <span>
                            {assortmentDetails.rule_failures[0]?.failureType}
                          </span>
                        </div>
                        <div style={{ width: "120px", height: "70px", fontSize: "14px" }}>
                          {!isReadOnlyUser && (
                            <button
                              onClick={handleCheckRuleWithCheck}
                              style={{
                                border: "1px solid  #14AE78",
                                backgroundColor: "transparent",
                                color: "#14AE78",
                                padding: "8px",
                                borderRadius: "12px"
                              }}
                            >
                              Check Again
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="warehouse-assortment-items-wrapper">
                  <div
                    className={`warehouse-assortment-items-container ${autoScrollDirection ? `auto-scroll-${autoScrollDirection}` : ''} ${isReadOnlyUser ? 'read-only' : ''}`}
                    ref={itemsContainerRef}
                  >
                    <div className="warehouse-assortment-primary-section">
                      <div className="warehouse-assortment-section-header">
                        <h3>Primary Assortment ({currentItems.length} items)</h3>
                      </div>
                      <div className="warehouse-assortment-items-grid">
                        {currentItems.map((item, index) => (
                          <div
                            key={`primary-${item.mv_id}-${index}`}
                            className={`warehouse-assortment-item-card ${item.hasRankChanged ? 'rank-changed' : ''} ${isReadOnlyUser ? 'read-only-item' : ''}`}
                            draggable={!isReadOnlyUser}
                            onDragStart={(e) => handleDragStart(e, item, 'primary')}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                          >
                            <div className="warehouse-assortment-item-index">
                              {item.rank < 10 ? `0${item.rank}` : item.rank}
                            </div>

                            {renderSlotIcon(item.rank)}

                            <div className="warehouse-assortment-item-content-wrapper">
                              <div className="warehouse-assortment-item-content">
                                <div className="warehouse-assortment-item-image">
                                  {item.image ? (
                                    <img
                                      src={`https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/${item.image}`}
                                      alt={item.variant_name}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = "none";
                                        e.target.parentNode.innerHTML = "<span>No Image</span>";
                                      }}
                                    />
                                  ) : (
                                    <span>No Image</span>
                                  )}
                                </div>
                                <div className="warehouse-assortment-item-details">
                                  <div className="warehouse-assortment-item-name">
                                    {item.variant_name}
                                  </div>
                                  <div className="warehouse-assortment-item-meta">
                                    <span className="warehouse-assortment-item-mvid">
                                      MVID: {item.mv_id}
                                    </span>
                                    <span className="warehouse-assortment-item-price">
                                      ₹{item.offer_price}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {item.hasRankChanged && item.originalRank && (
                                <div className="warehouse-assortment-rank-change">
                                  <span className="rank-change-text">
                                    Moved from rank {item.originalRank} ➜ {item.rank}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {remainingItems.length > 0 && (
                      <div className="warehouse-assortment-secondary-section">
                        <div className="warehouse-assortment-section-header">
                          <h3>Secondary Assortment ({remainingItems.length} items)</h3>
                          {!isReadOnlyUser && <p>Drag items to add them to the primary assortment</p>}
                        </div>
                        <div className="warehouse-assortment-items-grid secondary-items-grid">
                          {remainingItems.map((item, index) => (
                            <div
                              key={`secondary-${item.mv_id}-${index}`}
                              className={`warehouse-assortment-item-card secondary-item ${item.hasRankChanged ? 'rank-changed' : ''} ${isReadOnlyUser ? 'read-only-item' : ''}`}
                              draggable={!isReadOnlyUser}
                              onDragStart={(e) => handleDragStart(e, item, 'secondary')}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, index)}
                              onDragEnd={handleDragEnd}
                            >
                              <div className="warehouse-assortment-item-index">
                                {item.rank < 10 ? `0${item.rank}` : item.rank}
                              </div>

                              <div className="warehouse-assortment-item-content-wrapper">
                                <div className="warehouse-assortment-item-content">
                                  <div className="warehouse-assortment-item-image">
                                    {item.image ? (
                                      <img
                                        src={`https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/${item.image}`}
                                        alt={item.variant_name}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.style.display = "none";
                                          e.target.parentNode.innerHTML = "<span>No Image</span>";
                                        }}
                                      />
                                    ) : (
                                      <span>No Image</span>
                                    )}
                                  </div>
                                  <div className="warehouse-assortment-item-details">
                                    <div className="warehouse-assortment-item-name">
                                      {item.variant_name}
                                    </div>
                                    <div className="warehouse-assortment-item-meta">
                                      <span className="warehouse-assortment-item-mvid">
                                        MVID: {item.mv_id}
                                      </span>
                                      <span className="warehouse-assortment-item-price">
                                        ₹{item.offer_price}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {item.hasRankChanged && item.originalRank && (
                                  <div className="warehouse-assortment-rank-change">
                                    <span className="rank-change-text">
                                      Moved from rank {item.originalRank} ➜ {item.rank}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {!isReadOnlyUser && hasChanges && (
                  <div className="warehouse-assortment-save-changes-bar">
                    <div className="warehouse-assortment-save-message">
                      <img
                        src={dragdropimage}
                        alt="Drag and drop"
                        className="warehouse-assortment-drag-icon"
                      />
                      Note: To change items priority you can drag & drop items card from the list.
                    </div>
                    <div className="warehouse-assortment-save-actions">
                      <button
                        className="warehouse-assortment-cancel-button"
                        onClick={onCancelChanges}
                      >
                        Cancel
                      </button>
                      <button
                        className="warehouse-assortment-save-button"
                        onClick={onSaveChanges}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CohortAssortmentDetailsComponent;